import { useCallback, useEffect, useRef, useState } from "react";
import { publicWebOrigin } from "@/utils";

interface Options {
  src: string;
  message: object;
  heightMessageType?: string;
  defaultHeight?: number;
}

const useIframePreview = ({ src, message, heightMessageType, defaultHeight = 0 }: Options) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [connectedCount, setConnectedCount] = useState(0);
  const [iframeHeight, setIframeHeight] = useState(defaultHeight);
  const iframeOrigin = publicWebOrigin();

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== iframeOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (heightMessageType && event.data?.type === heightMessageType && typeof event.data.height === "number") {
        setIframeHeight(event.data.height);
        return;
      }
      setConnectedCount(prev => prev + 1);
    },
    [iframeOrigin, heightMessageType]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    setIframeHeight(defaultHeight);
    setConnectedCount(0);
  }, [src, defaultHeight]);

  useEffect(() => {
    if (!connectedCount || !iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(message, iframeOrigin);
  }, [connectedCount, message, iframeOrigin]);

  return { iframeRef, iframeHeight, iframeOrigin };
};

export { useIframePreview };
